// Registro dos tours guiados (driver.js). Cada tour: passos com seletor do
// elemento REAL da tela + chaves i18n (TOUR.<TOUR>.<PASSO>.TITLE/TEXT).
// Passos cujo elemento não existe na tela são pulados na hora de montar —
// mudou a UI, o tour degrada com elegância em vez de quebrar.
// `click: true` = passo interativo (só avança clicando no elemento destacado).
// `lazy: true` = elemento só existe depois de uma ação (não filtra no build).
//
// ⚠️ Âncoras do menu: data-tour="sidebar-<name>" usa o NAME INTERNO do item
// (inglês, ex.: 'Conversation', 'AiAgents'), não o rótulo traduzido.

// Rota (route name) → tour que se aplica àquela tela.
export const TOURS_BY_ROUTE = {
  // CRM
  pipeline_view: 'pipeline',
  pipeline_funnel_view: 'pipeline',
  pipeline_deal: 'deal',
  pipeline_calendar: 'calendar',
  crm_home: 'crm_home',
  companies_dashboard_index: 'companies',
  products_list: 'products',
  // Atendimento
  home: 'conversations',
  inbox_conversation: 'conversations',
  inbox_dashboard: 'conversations',
  conversation_through_inbox: 'conversations',
  main_dashboard: 'dashboard',
  chat_rooms_index: 'chatrooms',
  chat_rooms: 'chatrooms',
  // IA
  ai_agents_list: 'agents',
  ai_agents_flow: 'canvas',
  // Contatos
  contacts_dashboard_index: 'contacts',
  contacts_dashboard_segments_index: 'contacts',
  contacts_dashboard_labels_index: 'contacts',
  contacts_dashboard_active: 'contacts',
  // Marketing
  campaigns_ongoing_index: 'campaigns',
  campaigns_one_off_index: 'campaigns',
  campaigns_whatsapp_index: 'campaigns',
  growth_links_list: 'growth',
  prospecting_lists_index: 'prospecting',
  prospecting_lists_show: 'prospecting',
  // Relatórios
  account_overview_reports: 'reports',
  agent_reports: 'reports',
  label_reports: 'reports',
  inbox_reports: 'reports',
  team_reports: 'reports',
  // Configurações
  settings_home: 'settings',
  settings_inbox_list: 'inboxes',
  agent_list: 'team',
  labels_list: 'labels',
  automation_list: 'automation',
  settings_integrations: 'integrations',
  canned_list: 'canned',
  macros_wrapper: 'macros',
  attributes_list: 'attributes',
  billing_settings_index: 'billing',
  voices_list: 'voices',
  csat_reports: 'reports',
  kanban_reports: 'reports',
};

export const TOUR_DEFINITIONS = {
  overview: {
    steps: [
      { key: 'WELCOME' },
      { key: 'DASHBOARD', element: '[data-tour="sidebar-dashboard"]' },
      { key: 'CONVERSAS', element: '[data-tour="sidebar-conversation"]' },
      { key: 'CRM', element: '[data-tour="sidebar-crm"]' },
      { key: 'AGENTES', element: '[data-tour="sidebar-aiagents"]' },
      { key: 'CHAT_INTERNO', element: '[data-tour="sidebar-chatrooms"]' },
      { key: 'CONTATOS', element: '[data-tour="sidebar-contacts"]' },
      { key: 'CAMPANHAS', element: '[data-tour="sidebar-marketing"]' },
      { key: 'RELATORIOS', element: '[data-tour="sidebar-reports"]' },
      {
        key: 'CTA',
        element: '[data-tour="sidebar-conversation"]',
        click: true,
      },
    ],
  },
  conversations: {
    steps: [
      { key: 'INTRO' },
      { key: 'LISTA', element: '.conversations-list-wrap' },
      {
        key: 'CAIXA',
        element: '.conversation-details-wrap, .messages-and-sidebar',
      },
      { key: 'RESPOSTA', element: '.reply-box' },
      { key: 'FIM' },
    ],
  },
  pipeline: {
    steps: [
      { key: 'INTRO' },
      { key: 'FUNIL', element: '[data-tour="pipeline-funil"]' },
      { key: 'BOARD', element: '.kanban-board' },
      { key: 'CARD', element: '.kanban-card', click: true },
    ],
  },
  deal: {
    steps: [
      { key: 'INTRO' },
      { key: 'HEADER', element: '[data-tour="deal-header"]' },
      { key: 'ACOES', element: '[data-tour="deal-acoes"]' },
      { key: 'CONVERSA', element: '.reply-box' },
      { key: 'FIM' },
    ],
  },
  calendar: {
    steps: [
      { key: 'INTRO' },
      { key: 'CAL', element: '.calendar-container' },
      {
        key: 'NOVO',
        element: '[data-tour="agenda-novo"]',
        click: true,
        delay: 600,
      },
      // lazy: o modal só existe DEPOIS do clique do passo anterior.
      { key: 'MODAL', element: '.modal-container', lazy: true },
      { key: 'FIM' },
    ],
  },
  crm_home: { steps: [{ key: 'INTRO' }, { key: 'ATALHOS' }, { key: 'FIM' }] },
  companies: { steps: [{ key: 'INTRO' }, { key: 'VINCULO' }, { key: 'FIM' }] },
  products: { steps: [{ key: 'INTRO' }, { key: 'VALOR' }, { key: 'FIM' }] },
  dashboard: { steps: [{ key: 'INTRO' }, { key: 'NUMEROS' }, { key: 'FIM' }] },
  chatrooms: { steps: [{ key: 'INTRO' }, { key: 'SALAS' }, { key: 'FIM' }] },
  agents: {
    steps: [
      { key: 'INTRO' },
      { key: 'CRIAR' },
      { key: 'TESTAR' },
      { key: 'FIM' },
    ],
  },
  canvas: {
    steps: [
      { key: 'INTRO' },
      { key: 'PALETA', element: '[data-tour="canvas-paleta"]' },
      { key: 'BLOCOS' },
      { key: 'FIM' },
    ],
  },
  contacts: {
    steps: [
      { key: 'INTRO' },
      { key: 'PERFIL' },
      { key: 'ORGANIZAR' },
      { key: 'FIM' },
    ],
  },
  campaigns: {
    steps: [
      { key: 'INTRO' },
      { key: 'NOVA', element: '[data-tour="campanhas-nova"]' },
      { key: 'PASSOS' },
      { key: 'FIM' },
    ],
  },
  growth: { steps: [{ key: 'INTRO' }, { key: 'CANAIS' }, { key: 'FIM' }] },
  reports: { steps: [{ key: 'INTRO' }, { key: 'TIPOS' }, { key: 'FIM' }] },
  inboxes: {
    steps: [
      { key: 'INTRO' },
      { key: 'STATUS' },
      { key: 'NOVA', element: '[data-tour="inboxes-nova"]' },
      { key: 'FIM' },
    ],
  },
  prospecting: {
    steps: [
      { key: 'INTRO' },
      { key: 'BUSCA' },
      { key: 'CUSTO' },
      { key: 'FIM' },
    ],
  },
  settings: { steps: [{ key: 'INTRO' }, { key: 'MAPA' }, { key: 'FIM' }] },
  team: { steps: [{ key: 'INTRO' }, { key: 'PAPEIS' }, { key: 'FIM' }] },
  labels: { steps: [{ key: 'INTRO' }, { key: 'USO' }, { key: 'FIM' }] },
  automation: {
    steps: [{ key: 'INTRO' }, { key: 'EXEMPLOS' }, { key: 'FIM' }],
  },
  integrations: {
    steps: [{ key: 'INTRO' }, { key: 'PRINCIPAIS' }, { key: 'FIM' }],
  },
  canned: { steps: [{ key: 'INTRO' }, { key: 'USO' }, { key: 'FIM' }] },
  macros: { steps: [{ key: 'INTRO' }, { key: 'FIM' }] },
  attributes: { steps: [{ key: 'INTRO' }, { key: 'FIM' }] },
  billing: { steps: [{ key: 'INTRO' }, { key: 'FIM' }] },
  voices: { steps: [{ key: 'INTRO' }, { key: 'FIM' }] },
};
